import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useEnterprisePartnerIdentities,
} from '@/hooks/useEnterprisePartners';
import { useDeprovisionIdentity } from '@/hooks/useEnterpriseSsoActions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';

interface PartnerIdentityListProps {
  partnerId: string;
  /** Provisioning mode — affects how provisioned_workspace_id is rendered. */
  partnerMode: 'shared' | 'per_user';
}

interface IdentityRow {
  id: string;
  partner_id: string;
  external_user_id: string;
  email: string;
  buildit_user_id: string | null;
  provisioned_workspace_id: string | null;
  status: string;
  created_at: string;
  last_login_at: string | null;
  revoked_at: string | null;
}

function statusBadgeVariant(
  status: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active':
      return 'default';
    case 'revoked':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function PartnerIdentityList({
  partnerId,
  partnerMode,
}: PartnerIdentityListProps): JSX.Element {
  const { data, isLoading, error, refetch } =
    useEnterprisePartnerIdentities(partnerId);
  const deprovision = useDeprovisionIdentity();
  const { toast } = useToast();

  const [confirmTarget, setConfirmTarget] = useState<IdentityRow | null>(null);

  const identities = (data ?? []) as IdentityRow[];
  const showWorkspaceColumn = partnerMode === 'per_user';

  const handleConfirmDeprovision = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    try {
      await deprovision.mutateAsync({
        partner_id: partnerId,
        external_user_id: target.external_user_id,
      });
      toast({ description: `Deprovisioned ${target.email}` });
      setConfirmTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      captureError(err as Error, { feature: 'enterprise-partner-identity-list-handle-confirm-deprovision' });
      toast({
        description: `Failed: ${message}`,
        variant: 'destructive',
      });
      setConfirmTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-10 animate-pulse rounded bg-muted" />
        <div className="h-10 animate-pulse rounded bg-muted" />
        <div className="h-10 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3 rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        <p>
          Failed to load provisioned identities:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (identities.length === 0) {
    return (
      <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
        No employees provisioned yet. Once an enterprise user clicks the SSO
        button from their portal, they&rsquo;ll appear here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>External User ID</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Login</TableHead>
            {showWorkspaceColumn && <TableHead>Workspace</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {identities.map((row) => {
            const isRevoked = row.status === 'revoked';
            const lastLoginLabel = row.last_login_at
              ? formatDistanceToNow(new Date(row.last_login_at), {
                  addSuffix: true,
                })
              : 'Never';
            const isPendingThisRow =
              deprovision.isPending &&
              confirmTarget?.external_user_id === row.external_user_id;

            return (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs">
                  {row.external_user_id}
                </TableCell>
                <TableCell className="text-sm">{row.email}</TableCell>
                <TableCell>
                  <Badge variant={statusBadgeVariant(row.status)}>
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {lastLoginLabel}
                </TableCell>
                {showWorkspaceColumn && (
                  <TableCell className="font-mono text-xs">
                    {/* TODO: linkify to /workspace/${id} once workspace
                        routing is wired in. For v1 we display the raw UUID. */}
                    {row.provisioned_workspace_id ? (
                      <a
                        href={`/workspace/${row.provisioned_workspace_id}`}
                        className="text-primary hover:underline"
                      >
                        {row.provisioned_workspace_id}
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={isRevoked || isPendingThisRow}
                    onClick={() => setConfirmTarget(row)}
                  >
                    {isPendingThisRow ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Deprovisioning...
                      </>
                    ) : (
                      'Deprovision'
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/*
        TODO: cursor-based pagination via p_after_created_at when
        identities.length === 100 — for Phase 3 v1 we render the first
        page only.
      */}
      {identities.length === 100 && (
        <p className="text-xs text-muted-foreground">
          Showing first 100 identities. Pagination coming soon.
        </p>
      )}

      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deprovision.isPending) {
            setConfirmTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deprovision SSO access?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget && (
                <>
                  Deprovisioning revokes {confirmTarget.email}&rsquo;s SSO
                  access. They&rsquo;ll lose access to the partner workspace
                  immediately. Continue?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deprovision.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleConfirmDeprovision}
              disabled={deprovision.isPending}
              variant="destructive"
            >
              {deprovision.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deprovisioning...
                </>
              ) : (
                'Deprovision'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
